import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import axios from "../../../../api/axios";
import { useApiCache } from "../../../../hooks/useApiCache";
import { EMPLOYEE_OR_HIRED_FILTER } from "../hoursEmployeeFilters";

const FETCH_URL = "/users/";
const DEFAULT_FILTERS = [EMPLOYEE_OR_HIRED_FILTER];
const FIELDS =
  "id,displayTitle,first_name,last_name,user_type_id,email,job_title,title,position";

function initialsFrom(name) {
  if (!name) return "?";
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function roleLine(user, t) {
  if (!user) return "";
  const parts = [
    user.job_title || user.title || user.position || null,
    user.department?.name || user.department_name || null,
    user.user_type_id === 3 ? t("employeeSelector.hired_badge") : null,
  ].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return user.email || t("hours.sheet.employee_role_fallback");
}

function filtersCacheKey(filters) {
  try {
    return JSON.stringify(filters || []);
  } catch {
    return "default";
  }
}

/**
 * Design-faithful employee picker for Timeliste (uke-strip redesign).
 * Inline dropdown with avatar / name / role — not the PMDialog EmployeeSelector.
 * Uses useApiCache (5 min TTL) so reopen / re-render / day-change does not refetch.
 */
export function HoursSheetEmployeePicker({
  selected,
  onChange,
  disabled = false,
  weekHoursLabel = null,
  filters = DEFAULT_FILTERS,
}) {
  const { t } = useTranslation();
  const { getCachedData, setCachedData } = useApiCache();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const rootRef = useRef(null);
  const inflightRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const displayName =
    selected?.displayTitle ||
    [selected?.first_name, selected?.last_name].filter(Boolean).join(" ") ||
    t("hours.sheet.employee_none");
  const displayRole = roleLine(selected, t);
  const displayInitials = initialsFrom(displayName);

  const filtersKey = useMemo(() => filtersCacheKey(filters), [filters]);

  const baseFilters = useMemo(() => {
    const f = [EMPLOYEE_OR_HIRED_FILTER];
    if (Array.isArray(filters)) {
      filters.forEach((item) => {
        const name = String(item?.name || "").toLowerCase();
        if (name === "employee") return;
        f.push(item);
      });
    }
    return f;
    // filtersKey tracks filters content; avoid identity churn from parent arrays
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const cacheKeyFor = useCallback(
    (searchQuery) =>
      `hours_sheet_employees_${companyId || "nocompany"}_${filtersKey}_${searchQuery || "default"}`,
    [companyId, filtersKey]
  );

  const fetchItems = useCallback(
    async (searchQuery) => {
      const cacheKey = cacheKeyFor(searchQuery);
      const cached = getCachedData(cacheKey);
      if (cached) {
        setItems(cached);
        setLoading(false);
        return;
      }

      if (inflightRef.current === cacheKey) return;
      inflightRef.current = cacheKey;
      setLoading(true);

      try {
        const response = await axios({
          method: "get",
          url: FETCH_URL,
          params: {
            query: searchQuery || undefined,
            page: 1,
            per_page: 30,
            sortBy: "id",
            sort: "desc",
            filters: baseFilters,
            fields: FIELDS,
            showInDropdown: true,
          },
        });
        const rows = response.data?.data || [];
        setCachedData(cacheKey, rows);
        setItems(rows);
      } catch {
        setItems([]);
      } finally {
        if (inflightRef.current === cacheKey) inflightRef.current = null;
        setLoading(false);
      }
    },
    [baseFilters, cacheKeyFor, getCachedData, setCachedData]
  );

  const debouncedFetch = useMemo(
    () => debounce((q) => fetchItems(q), 280),
    [fetchItems]
  );

  // Prefetch default list on mount / company change (privileged picker only).
  useEffect(() => {
    if (disabled) return;
    inflightRef.current = null;
    const cacheKey = cacheKeyFor("");
    const cached = getCachedData(cacheKey);
    if (cached) {
      setItems(cached);
      return;
    }
    setItems([]);
    fetchItems("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, companyId, cacheKeyFor]);

  useEffect(() => {
    if (!open || disabled) return undefined;
    const cacheKey = cacheKeyFor(query);
    const cached = getCachedData(cacheKey);
    if (cached) {
      setItems(cached);
      setLoading(false);
      return undefined;
    }
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, disabled, debouncedFetch, cacheKeyFor, getCachedData]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const handlePick = (user) => {
    onChange?.(user);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="hts-emp" ref={rootRef} data-emp="">
      <button
        type="button"
        className={`hts-emp-trigger${open ? " is-open" : ""}`}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          if (!open) setQuery("");
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="hts-emp-avatar">{displayInitials}</span>
        <span style={{ minWidth: 0 }}>
          <span className="hts-emp-name">{displayName}</span>
          <span className="hts-emp-role">{displayRole}</span>
        </span>
        {!disabled && <span className="hts-emp-chevron">▾</span>}
      </button>

      {open && !disabled && (
        <div className="hts-emp-menu" role="listbox">
          <div className="hts-emp-search">
            <span aria-hidden>⌕</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("hours.sheet.employee_search")}
              autoFocus
            />
          </div>
          <div className="hts-emp-list">
            {loading && items.length === 0 ? (
              <div className="hts-emp-empty">{t("hours.quick.loading")}</div>
            ) : items.length === 0 ? (
              <div className="hts-emp-empty">{t("hours.sheet.employee_empty")}</div>
            ) : (
              items.map((user) => {
                const name =
                  user.displayTitle ||
                  `${user.first_name || ""} ${user.last_name || ""}`.trim();
                const selectedId = selected?.id != null ? String(selected.id) : null;
                const isSel = selectedId != null && String(user.id) === selectedId;
                const weekLabel =
                  isSel && weekHoursLabel != null ? weekHoursLabel : null;
                return (
                  <button
                    key={user.id}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    className={`hts-emp-row${isSel ? " is-selected" : ""}`}
                    onClick={() => handlePick(user)}
                  >
                    <span className="hts-emp-row-avatar">{initialsFrom(name)}</span>
                    <span style={{ minWidth: 0 }}>
                      <span
                        className="hts-emp-name"
                        style={{ fontSize: 13, fontWeight: 600 }}
                      >
                        {name}
                      </span>
                      <span className="hts-emp-role">{roleLine(user, t)}</span>
                    </span>
                    {weekLabel != null && (
                      <span className="hts-emp-week">{weekLabel}</span>
                    )}
                    <span className="hts-emp-check">✓</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
